<script setup lang="ts">
import { armarUrl } from '@idem/common/armar-url';
import { GOOGLE_MAPS_API_KEY } from '@idem/common/constantes';
import { MACROCATEGORIAS } from '@idem/common/macrocategorias';
import GoogleAddressAutocomplete from 'vue3-google-address-autocomplete';
import FeatureFlag from '@frontend/common/feature-flag.vue';

defineProps<{
  actividad: any;
  saving?: boolean;
  permitirGestion: boolean;
}>();

const editState = defineModel<any>('editState');

const emit = defineEmits<{
  addressSelected: [direccion: any];
  photoSelected: [event: Event];
  cropExistingPhoto: [];
  toggleEdit: [];
  save: [];
  manage: [accion: 'aprobar' | 'reprobar' | 'eliminar'];
}>();
</script>

<template>
  <div :data-testid="`actividad-${actividad.id}`" class="mb-4 rounded border p-4 shadow-sm">
    <template v-if="editState">
      <label class="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
      <input v-model="editState.draft.nombre" class="w-full rounded border px-3 py-2" />
    </template>
    <h2 v-else class="text-xl font-semibold">{{ actividad.nombre }}</h2>
    <template v-if="editState">
      <GoogleAddressAutocomplete
        :apiKey="GOOGLE_MAPS_API_KEY || 'NO_API_KEY_PROVIDED'"
        v-model="editState.draft.direccion"
        :value="editState.draft.direccion"
        class="mt-2 w-full rounded border px-3 py-2"
        placeholder="Calle 123, Ciudad"
        @keydown.enter="e => e.preventDefault()"
        @callback="direccion => emit('addressSelected', direccion)"
      />
      <label class="mt-2 block text-sm text-gray-600">
        Foto
        <input type="file" accept="image/jpeg,image/png" class="mt-1 w-full rounded border px-3 py-2" @change="emit('photoSelected', $event)" />
      </label>
      <img
        v-if="actividad.foto || editState.draft.fotoPreview"
        :src="editState.draft.fotoPreview || armarUrl(actividad.foto)"
        alt="foto" class="my-2 h-64 w-full max-w-[360px] cursor-pointer object-cover"
        @click="emit('cropExistingPhoto')"
      />
    </template>
    <img v-else-if="actividad.foto" :src="armarUrl(actividad.foto)" alt="foto" class="my-2 h-64 w-full max-w-[360px] object-cover" />
    <textarea v-if="editState" v-model="editState.draft.descripcion" class="mt-2 w-full rounded border px-3 py-2" aria-label="Descripción" />
    <p v-else class="text-sm text-gray-700">{{ actividad.descripcion }}</p>
    <p v-if="!editState" class="text-sm text-gray-600">Dirección: {{ actividad.direccion || actividad.direccionActividad }}</p>
    <div v-if="editState" class="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
      <label class="text-sm text-gray-600">Fecha<input v-model="editState.draft.fecha" type="date" class="mt-1 w-full rounded border px-3 py-2" /></label>
      <label class="text-sm text-gray-600">Hora<input v-model="editState.draft.hora" type="time" class="mt-1 w-full rounded border px-3 py-2" /></label>
    </div>
    <p v-else class="text-sm text-gray-600">Fecha: {{ actividad.fecha }} · Hora: {{ actividad.hora }}</p>
    <template v-if="editState">
      <label class="mt-2 block text-sm text-gray-600">Link<input v-model="editState.draft.link" type="text" class="mt-1 w-full rounded border px-3 py-2" /></label>
    </template>
    <p v-else-if="actividad.link" class="text-sm text-gray-600">Link: {{ actividad.link }}</p>
    <feature-flag name="macrocategoria-al-visar">
      <template #funcionalidadHabilitada>
        <label v-if="editState" class="mt-2 block text-sm text-gray-600">
          Macrocategoría
          <select v-model="editState.draft.macrocategoria" data-testid="macrocategoria" class="mt-1 w-full rounded border px-3 py-2">
            <option value="">Sin macrocategoría</option>
            <option v-for="macrocategoria in MACROCATEGORIAS" :key="macrocategoria" :value="macrocategoria">{{ macrocategoria }}</option>
          </select>
        </label>
        <p v-else-if="actividad.macrocategoria" class="text-sm text-gray-600">Macrocategoría: {{ actividad.macrocategoria }}</p>
      </template>
    </feature-flag>
    <div class="mt-2 flex gap-2">
      <button @click="emit('toggleEdit')" class="rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700">{{ editState ? 'Cancelar' : 'Editar' }}</button>
      <button v-if="editState" :disabled="saving" @click="emit('save')" class="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">{{ saving ? 'Guardando...' : 'Guardar' }}</button>
      <template v-if="!editState && permitirGestion">
        <button @click="emit('manage', 'aprobar')" class="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700">Aprobar</button>
        <button @click="emit('manage', 'reprobar')" class="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">Reprobar</button>
        <button @click="emit('manage', 'eliminar')" class="rounded-md bg-red-800 px-4 py-2 text-sm font-semibold text-white hover:bg-red-900">Eliminar</button>
      </template>
    </div>
  </div>
</template>
